This is the exact syntax of a `.canary.md` spec file — what the parser actually matches, not
a loose description. A spec that reads as good prose but drifts from this grammar does NOT
error at parse time in most cases: the parser drops the line/bullet it can't match and reports
only a warn-severity issue. A spec can validate as "ok" while silently missing content the
author wrote. When in doubt, match the grammar below character-for-character rather than
writing what merely reads naturally.

FRONTMATTER

  A `---`-fenced YAML block, first thing in the file:

    ---
    name: Org onboarding — invite a member
    workflowId: <uuid>          # present only once compiled; NEVER remove or edit it by hand
    status: draft               # draft | approved
    flowType: standard          # standard | setup | seed | teardown
    actor: admin                # template role name, "persona <role>", "credential <name>",
                                 # or "seed-output <ref>" — see Switch role below
    authenticated: true
    tags: [smoke, p0]
    requirementRef: LIN-4421
    ---

  `workflowId` binds the file to a live workflow: present → `spec_compile` diffs/updates that
  workflow; absent → `spec_compile` creates a new one. If it's present, treat it as immutable —
  removing it turns your next compile into a duplicate-creating CREATE instead of an UPDATE.

SECTION HEADINGS (H2, in this order)

  ## Spirit       — one blockquote paragraph: the user-visible promise this flow proves.
  ## Preflight     — plain prose or bullets: non-seedable preconditions the environment must
                     already satisfy.
  ## Cleanup       — plain prose or bullets: how the run's side effects get reset.
  ## Inputs        — bullets: `- <name>: <defaultValue>` or `- credential <name>: <value>`.
  ## Scenarios     — H3 sub-blocks per scenario: `### <Name> *(default)*` or `*(skip: <reason>)*`,
                     with `- values: {...}` / `- credentials: {...}` YAML underneath.
  ## Journey       — the linear sequence of nodes described below. Required — a spec with no
                     Journey nodes fails to parse.

JOURNEY LEAD-INS

  Every Journey line is either a bold lead-in (`**...**`) or a blockquote/bullet attached to
  the preceding lead-in. Blank lines between a lead-in, its description, and its bullets are
  fine — the parser skips them.

    **Navigate to** /path/here
    **Action — Fill out the signup form**
    **Assert** the confirmation toast is visible
    **Wait** 30s                        (also accepts 5m, 2h)
    **End**                             (add "fail" anywhere in the line for a failing End)
    **Seed** seed-workflow-name
    **Setup** setup-workflow-name
    **Switch role to** admin            (or **Switch role to admin** — both forms work)
    **Sandbox action — provision-org**
    **Api sequence — Create via API**
    **If** $var.status is "blocked"
    **Then:**
      <nested nodes, indented 2 spaces>
    **Else:**
      <nested nodes, indented 2 spaces>

  CRITICAL: for `Action`, `Sandbox action`, and `Api sequence`, the title goes AFTER an em-dash
  (`—`) or hyphen INSIDE the bold span: `**Action — Title**`. Writing `**Action** Title` (title
  outside the bold) used to silently lose the title; it is now tolerated as a fallback, but the
  em-dash-inside-bold form is canonical — use it. Do not invent a form for a lead-in not listed
  above (e.g. there is no `**Click**` or `**Type**` lead-in) — express it as an Action instead.

DESCRIPTION (optional, any lead-in)

  A blockquote line immediately after the lead-in (or after a blank line) becomes the node's
  description — free prose is fine and expected here:

    **Action — Create the org**
    > Fill the org form with orgName and plan, then submit.
    - field name: $orgName

ACTION BULLET GRAMMAR (exact — only these forms parse; everything else is DROPPED)

    - field <name> [(type)]: <value>       e.g. - field plan (select): $plan
    - capture: <name>                      e.g. - capture: createdOrgId
    - assert (warn|fail|fatal[, agentic]): <condition>
                                            e.g. - assert (fail): URL ends with `/dashboard`
                                            e.g. - assert (warn, agentic): chart looks right
    - alwaysUseAgent: true
    - captureAudio: true
    - captureLabel: <label>
    - captureExtraMs: <number>

  A standalone `**Assert**` node takes its own kv option bullet the same way:

    - severity: warn|fail|fatal            (assert nodes only; NOT `- severity: warn — text`,
                                            which is an unparseable kv bullet, not an assert)

  `Seed`/`Setup` bullets: `- version: latest`, `- mode: ...`, `- onFailure: block|warn`,
  `- input.<name>: <value>`. `Sandbox action` bullets: `- label: ...`, `- outputs: a, b`,
  `- param.<name>: <value>`.

COMPLETE MINIMAL EXAMPLE

    ---
    name: Login smoke
    actor: admin
    authenticated: true
    requirementRef: LIN-101
    ---

    ## Spirit
    > A returning admin can sign in and reach the dashboard.

    ## Inputs
    - adminEmail: $EMAIL

    ## Journey

    **Navigate to** `/login`.

    **Action — Sign in**
    > Enter email and password, submit.
    - field email: $adminEmail
    - field password: $CREDENTIAL
    - assert (fail): URL ends with `/dashboard`

    **End**.

COMMON MISTAKES (each of these compiles into an empty or half-empty node — no parse error)

  - A free-text bullet ("- Should reach the dashboard eventually") is not `field`/`capture`/
    `assert (...)`/a known kv key — it is DROPPED. Put prose in the `>` description instead of
    inventing a bullet shape.
  - `- severity: warn — should show a toast` is a kv bullet with key `severity`; it is not a
    recognized action option, so it is DROPPED. Use `- assert (warn): should show a toast`.
  - `**Action** Do the thing` (title outside the bold) is tolerated but non-canonical — prefer
    `**Action — Do the thing**`.
  - Any Journey line that doesn't start with a recognized bold lead-in is DROPPED with only a
    warn-severity "Unexpected line in Journey" issue — it will not error your compile.
  - After authoring or editing a spec, always run `spec_validate` and inspect its
    `droppedContent` field. Non-empty `droppedContent` means real content is about to be lost —
    treat it as a blocking error, not a warning, even though its underlying severity is `warn`.
